import type { BookingNotification } from '@/contexts/websocket';
import type { VendorAppNotification } from '@/services/notifications';

/**
 * La bandeja junta dos listas: los avisos que llegaron por WebSocket en esta sesión (guardados en
 * el navegador hasta 30 días) y los de la API (los 50 más nuevos de la base). Dibujarlas una
 * arriba de la otra mostraba reservas de hace semanas encima de las de hace una hora en cuanto un
 * aviso en tiempo real se quedaba sin su par en la página de la API (negocios con más de 50
 * avisos nuevos). Acá se mezclan por fecha, de la más nueva a la más vieja.
 */
export type MergedNotification =
  | { kind: 'realtime'; notification: BookingNotification; index: number }
  | { kind: 'api'; notification: VendorAppNotification };

const notificationTime = (value: string | Date | undefined | null): number => {
  if (value === undefined || value === null) return Number.POSITIVE_INFINITY;
  const time = new Date(value).getTime();
  // Sin fecha legible: recién llegado, arriba de todo.
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

export const mergedNotificationTime = (item: MergedNotification): number =>
  item.kind === 'realtime'
    ? notificationTime(item.notification.createdAt)
    : notificationTime(item.notification.data?.createdAt ?? item.notification.created_at);

export const mergeNotificationsByDate = (
  realtime: BookingNotification[],
  api: VendorAppNotification[]
): MergedNotification[] => {
  const items: MergedNotification[] = [
    ...realtime.map((notification, index) => ({ kind: 'realtime' as const, notification, index })),
    ...api.map((notification) => ({ kind: 'api' as const, notification }))
  ];
  // `sort` es estable: a igual fecha queda el orden de llegada (tiempo real antes que la API).
  return items.sort((a, b) => mergedNotificationTime(b) - mergedNotificationTime(a));
};

// La lista inserta al frente: con el índice como clave, cada aviso nuevo remontaba todas las
// filas. La clave es el aviso (tipo + turno + momento); el índice solo si no hay fecha.
export const realtimeNotificationKey = (notification: BookingNotification, index: number): string => {
  const at = new Date(notification.createdAt).getTime();
  return `realtime-${notification.type ?? 'booking'}-${notification.bookingId}-${
    Number.isNaN(at) ? index : at
  }`;
};
